/**
 * NumberField.tsx
 *
 * Unified number input field component
 * Handles: number, integer, decimal, percentage formats
 */

import React from 'react';
import { BaseFieldProps, BASE_INPUT_CLASSES, ERROR_INPUT_CLASSES, DISABLED_INPUT_CLASSES } from './shared/types';
import { FieldWrapper } from './shared';

export const NumberField: React.FC<BaseFieldProps> = React.memo(({
  fieldConfig,
  mode,
  value,
  onChange,
  errors = [],
  disabled = false,
  hasError = false,
}) => {
  const isPreviewMode = mode === 'preview';
  const isDisabled = disabled || isPreviewMode;

  const numberMaxLength = fieldConfig.validation?.defaultMaxLength || 30;
  const numberFormat = fieldConfig.validation?.format;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isPreviewMode) return;

    const newValue = e.target.value;
    let isValid = false;

    if (numberFormat === 'integer') {
      // Integer: only digits and optional minus sign
      isValid = /^-?\d*$/.test(newValue);
    } else if (numberFormat === 'percentage') {
      // Percentage: digits, decimal point, optional % sign
      isValid = /^\d*\.?\d*%?$/.test(newValue);
    } else {
      // Decimal (default): digits, decimal point, optional minus sign
      isValid = /^-?\d*\.?\d*$/.test(newValue);
    }

    if (isValid && newValue.length <= numberMaxLength) {
      onChange(fieldConfig.id, newValue);
    }
  };

  const inputClasses = `${BASE_INPUT_CLASSES} ${hasError ? ERROR_INPUT_CLASSES : ''} ${isDisabled ? DISABLED_INPUT_CLASSES : ''}`;

  const inputElement = (
    <input
      id={fieldConfig.id}
      type="text"
      inputMode="decimal"
      value={value || ''}
      onChange={handleChange}
      onFocus={(e) => {
        if (!isPreviewMode) {
          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }}
      maxLength={numberMaxLength}
      placeholder={isPreviewMode ? fieldConfig.placeholder || 'Enter a number' : fieldConfig.placeholder}
      disabled={isDisabled}
      className={inputClasses}
    />
  );

  return (
    <FieldWrapper
      field={fieldConfig}
      errors={errors}
      hasError={hasError}
      isPreview={isPreviewMode}
    >
      {inputElement}
    </FieldWrapper>
  );
});

NumberField.displayName = 'NumberField';
